import { CartUpdateEvent } from '@theme/events';
import { morphSection } from '@theme/section-renderer';

/**
 * VELUNE CART UPSELL V3 — PRESET SOCKS TRIO
 * Black + Milk White + Dark Coffee added in one action.
 * The existing VELUNEUPSELL50 code is verified on all three lines before the
 * drawer is allowed to keep the upsell.
 */
class VeluneCartUpsell extends HTMLElement {
  #adding = false;
  #bound = false;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    if (!this.#bound) return;
    this.removeEventListener('click', this.#handleClick);
    this.#bound = false;
  }

  #ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  #handleClick = (event) => {
    const action = event.target.closest('[data-velune-action]');
    if (!(action instanceof HTMLElement) || !this.contains(action)) return;
    if (action.dataset.veluneAction !== 'add') return;

    event.preventDefault();
    event.stopPropagation();
    void this.#addTrio();
  };

  #trio() {
    return [
      { id: Number(this.dataset.blackVariantId), color: 'Black' },
      { id: Number(this.dataset.whiteVariantId), color: 'White' },
      { id: Number(this.dataset.brownVariantId), color: 'Brown' },
    ].filter((item) => Number.isFinite(item.id) && item.id > 0);
  }

  #setLoading(loading) {
    const button = this.#ref('quickAddButton');
    this.classList.toggle('is-loading', loading);

    if (button instanceof HTMLButtonElement) {
      button.disabled = loading;
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
  }

  #setError(message) {
    this.#adding = false;
    this.classList.remove('is-loading', 'is-added');
    this.#setLoading(false);

    const button = this.#ref('quickAddButton');
    if (button instanceof HTMLButtonElement) button.setAttribute('aria-checked', 'false');

    const status = this.#ref('status');
    if (status) status.textContent = message || 'Unable to add the socks trio. Please try again.';
  }

  #setSuccess() {
    this.classList.remove('is-loading');
    this.classList.add('is-added');
    this.dataset.added = 'true';

    const button = this.#ref('quickAddButton');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.removeAttribute('aria-busy');
      button.setAttribute('aria-checked', 'true');
    }
  }

  async #rollback(root, keys) {
    const cleanKeys = [...new Set(keys.filter(Boolean))];
    if (!cleanKeys.length) return;

    const updates = Object.fromEntries(cleanKeys.map((key) => [key, 0]));
    try {
      await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
    } catch (error) {
      console.error('[Velune] Unable to roll back socks trio:', error);
    }
  }

  async #addTrio() {
    if (this.#adding || this.dataset.added === 'true') return;

    const trio = this.#trio();
    if (trio.length !== 3) {
      this.#setError('The socks trio is temporarily unavailable.');
      return;
    }

    const cartItemsComponent = this.closest('cart-items-component');
    if (!(cartItemsComponent instanceof HTMLElement)) {
      this.#setError('Cart is not ready. Please try again.');
      return;
    }

    const sectionId = cartItemsComponent.dataset.sectionId;
    if (!sectionId) {
      this.#setError('Cart section is not ready. Please try again.');
      return;
    }

    this.#adding = true;
    this.#setLoading(true);
    const status = this.#ref('status');
    if (status) status.textContent = '';

    const root = window.Shopify?.routes?.root || '/';
    let addedKeys = [];

    try {
      const cartResponse = await fetch(`${root}cart.js`, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!cartResponse.ok) throw new Error('Unable to read cart. Please try again.');

      const currentCart = await cartResponse.json();
      const eligibleParents = currentCart.items.filter(
        (item) => String(item.properties?._velune_upsell) !== 'true' && Number(item.final_line_price) > 0
      );
      const parent =
        eligibleParents.find((item) =>
          (item.line_level_discount_allocations || []).some((allocation) =>
            String(allocation.discount_application?.title || '')
              .toLowerCase()
              .includes('velune final collection offer')
          )
        ) || eligibleParents[0];

      if (!parent) throw new Error('Add your Velune sandals before choosing this offer.');

      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: trio.map(({ id, color }) => ({
            id,
            quantity: 1,
            properties: {
              _velune_upsell: 'true',
              _velune_upsell_group: 'socks-trio',
              _velune_parent_variant: String(parent.variant_id),
              _velune_upsell_color: color,
            },
          })),
        }),
      });

      const addResult = await addResponse.json();
      if (!addResponse.ok) {
        throw new Error(addResult?.description || addResult?.message || 'Unable to add the socks trio.');
      }

      addedKeys = (addResult?.items || [])
        .filter((item) => String(item?.properties?._velune_upsell_group) === 'socks-trio')
        .map((item) => item.key)
        .filter(Boolean);

      const discountCode = this.dataset.discountCode || 'VELUNEUPSELL50';
      const discountResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: discountCode,
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      });

      const discountedCart = await discountResponse.json();
      if (!discountResponse.ok) {
        throw new Error(discountedCart?.description || discountedCart?.message || 'Unable to apply the 50% offer.');
      }

      const trioIds = new Set(trio.map((item) => item.id));
      const trioLines = (discountedCart.items || []).filter(
        (item) =>
          trioIds.has(Number(item.variant_id)) &&
          String(item.properties?._velune_upsell_group) === 'socks-trio'
      );

      addedKeys = trioLines.map((item) => item.key).filter(Boolean).length
        ? trioLines.map((item) => item.key).filter(Boolean)
        : addedKeys;

      const allThreePresent = new Set(trioLines.map((item) => Number(item.variant_id))).size === 3;
      const allHalfPrice =
        allThreePresent &&
        trioLines.every((item) => {
          const original = Number(item.original_line_price);
          const final = Number(item.final_line_price);
          if (!Number.isFinite(original) || !Number.isFinite(final)) return false;
          return final <= Math.round(original * 0.5) + 1;
        });

      if (!allHalfPrice) {
        await this.#rollback(root, addedKeys);
        addedKeys = [];
        throw new Error('The 50% socks offer could not be applied. Please try again.');
      }

      this.#setSuccess();
      addedKeys = [];

      cartItemsComponent.dispatchEvent(
        new CartUpdateEvent(discountedCart, sectionId, {
          itemCount: Number(discountedCart.item_count) || 0,
          source: 'velune-cart-upsell',
          sections: discountedCart.sections,
        })
      );

      const sectionHtml = discountedCart?.sections?.[sectionId];
      if (sectionHtml) {
        morphSection(sectionId, sectionHtml, { mode: 'hydration' });
      }

      this.#adding = false;
    } catch (error) {
      if (addedKeys.length) await this.#rollback(root, addedKeys);
      console.error('[Velune] Socks trio upsell failed:', error);
      this.#setError(error?.message);
    }
  }
}

if (!customElements.get('velune-cart-upsell')) {
  customElements.define('velune-cart-upsell', VeluneCartUpsell);
}
